import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons';
import { Table } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DataTablePaginationCursorProps<TData> {
  table: Table<TData>;
  isSelectPageSize?: boolean;
  hasMore?: boolean; // Added for cursor-based pagination
  currentPageIndex: number; // Current page index
}

export function DataTablePaginationCursor<TData>({
  table,
  isSelectPageSize = true,
  hasMore = false,
  currentPageIndex,
}: DataTablePaginationCursorProps<TData>) {
  // For cursor-based pagination, we can't know the total number of pages
  // So we'll just show the current page number
  const pageIndex = currentPageIndex;

  // We can go to the previous page if we're not on the first page
  const canPreviousPage = pageIndex > 0;
  
  // We can go to the next page if hasMore is true
  const canNextPage = hasMore;

  return (
    <div className="flex items-center justify-between px-2">
      {table.getFilteredSelectedRowModel().rows.length ? (
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{' '}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
      ) : (
        <div />
      )}
      <div className="flex items-center space-x-6 lg:space-x-8">
        {isSelectPageSize && (
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue
                  placeholder={table.getState().pagination.pageSize}
                />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 30, 40, 50].map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {pageIndex + 1}
          {/* No "of X" for cursor-based pagination */}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => table.setPageIndex(0)}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">Go to first page</span>
            <DoubleArrowLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.previousPage()}
            disabled={!canPreviousPage}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => table.nextPage()}
            disabled={!canNextPage}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
          {/* Hide Last Page button for cursor-based pagination */}
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0"
            onClick={() => {}}
            disabled={true}
          >
            <span className="sr-only">Go to last page</span>
            <DoubleArrowRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
} 